import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
 ArrowLeft,
 ArrowRight,
 AlertCircle,
 Check,
 Sparkles,
 ShieldCheck,
 Clock,
 Lock,
 Crown,
 Plus,
 Minus,
 TrendingUp,
 Zap,
 Award,
 Star,
 Repeat,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { LanguageProvider } from '../context/LanguageContext';
import { GoldBellMark } from '../components/NoFeeLogo';
import StripeRedirectOverlay from '../components/StripeRedirectOverlay';
import {
 StripeWordmark,
 PaymentNetworkRow,
} from '../components/PaymentLogos';
import DashboardPreview from '../components/DashboardPreview';
import SEO from '../components/SEO';
import OnboardingProgress from '../components/OnboardingProgress';
import FastInfoModal from '../components/FastInfoModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Founding Partner pricing — backend (billing_service.py) applies coupon
// FOUNDING_50_OFF_6M ($50 off for 6 months) on a $349/mo recurring price,
// effectively delivering $299/mo for the first 6 months, then $349/mo.
const FOUNDING_PRICE_USD = 299;
const REGULAR_PRICE_USD = 349;
const FOUNDING_MONTHS = 6;
const TRIAL_DAYS = 45;

const PLAN_INFO = {
 ordering: {
 label: 'NoFee Ordering — Founding Partner',
 foundingPrice: FOUNDING_PRICE_USD,
 regularPrice: REGULAR_PRICE_USD,
 foundingMonths: FOUNDING_MONTHS,
 trialDays: TRIAL_DAYS,
 },
 default: {
 label: 'NoFee — Founding Restaurant Partner',
 foundingPrice: FOUNDING_PRICE_USD,
 regularPrice: REGULAR_PRICE_USD,
 foundingMonths: FOUNDING_MONTHS,
 trialDays: TRIAL_DAYS,
 },
};

const FOUNDING_BENEFITS = [
 '0% commission on every order — forever',
 'Restaurants keep 100% of profits',
 'Direct Stripe payouts to your own accounts',
 'Setup in under 5 minutes',
 'Premium digital menu included',
 'Cancel anytime · no contracts',
];

const FAQS = [
 {
 q: 'How does the Founding Partner pricing work?',
 a: 'You lock in $299/month for your first 6 months — a $50/month discount off the standard $349/month rate. After month 6, your subscription automatically continues at the regular $349/month. You can cancel any time before that with one click.',
 },
 {
 q: 'When will I actually be charged?',
 a: 'Not today. Your 45-day free trial starts the moment you complete checkout. We collect your payment method to activate the account, but the first $299 charge only happens on day 46. Cancel before then and you pay nothing.',
 },
 {
 q: 'Can I cancel anytime?',
 a: 'Yes. Cancel from your dashboard in one click. No contracts, no cancellation fees, no questions asked. If you cancel during the trial, you are never charged.',
 },
 {
 q: 'When do customer payouts arrive?',
 a: 'Customer orders pay you directly through Stripe. Payments go directly to your own Stripe account. NoFee never holds your money. 0% commission, forever.',
 },
 {
 q: 'Is checkout secure?',
 a: 'Checkout is processed by Stripe with PCI-DSS Level 1 compliance and 256-bit TLS encryption. We never see or store your card.',
 },
];

// ───────────────────────────── FAQ Accordion ─────────────────────────────
const FaqAccordion = () => {
 const [open, setOpen] = useState(null);
 return (
 <div className="rounded-[20px] border border-stone-300/40 bg-white/60 backdrop-blur-md overflow-hidden" data-testid="complete-faq">
 {FAQS.map((f, i) => {
 const isOpen = open === i;
 return (
 <div key={i} className={i!== FAQS.length - 1? 'border-b border-stone-200/60': ''}>
 <button
 type="button"
 onClick={() => setOpen(isOpen? null: i)}
 data-testid={`faq-q-${i}`}
 className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/40 transition-colors"
 aria-expanded={isOpen}
 >
 <span className="text-[14px] font-bold text-neutral-800">{f.q}</span>
 {isOpen? (
 <Minus className="w-4 h-4 text-neutral-500 flex-shrink-0" />
 ): (
 <Plus className="w-4 h-4 text-neutral-500 flex-shrink-0" />
 )}
 </button>
 {isOpen && (
 <p className="px-5 pb-4 -mt-1 text-[13.5px] text-neutral-600 leading-[1.6]">{f.a}</p>
 )}
 </div>
 );
 })}
 </div>
 );
};

// ───────────────────────────── Main page ─────────────────────────────
const CompleteSubscriptionPage = () => {
 const { user, restaurant, token, loading: authLoading, logout } = useAuth();
 const navigate = useNavigate();
 const [params] = useSearchParams();
 const fromEmail = params.get('from') === 'email';
 const planParam = (params.get('plan') || '').toLowerCase();

 const planKey = planParam || restaurant?.signup_plan || 'ordering';
 const plan = PLAN_INFO[planKey] || PLAN_INFO.default;

 const [loading, setLoading] = useState(false);
 const [redirecting, setRedirecting] = useState(false);
 const [error, setError] = useState('');
 const [fastInfoOpen, setFastInfoOpen] = useState(false);

 useEffect(() => {
 if (!authLoading &&!token) {
 navigate(`/login?role=restaurant&next=complete-subscription${planParam? `&plan=${planParam}`: ''}`);
 }
 }, [authLoading, token, navigate, planParam]);

 useEffect(() => {
 const status = restaurant?.subscription_status;
 if (status && status!== 'pending_payment' && status!== '' && status!== 'none') {
 // Subscription is now active — advance the onboarding cursor to setup
 // so the dashboard banner reflects the correct remaining work.
 (async () => {
 try {
 await axios.post(
 `${API}/onboarding/advance`,
 { step: 'setup' },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 } catch {
 /* non-fatal */
 }
 })();
 navigate('/onboarding/setup');
 }
 }, [restaurant, navigate, token]);

 const handleResume = async () => {
 if (!token) return;
 setError('');
 setLoading(true);
 try {
 const origin = window.location.origin;
 const res = await axios.post(
 `${API}/billing/create-checkout-session`,
 { origin_url: origin },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 if (res.data?.url) {
 setRedirecting(true);
 setTimeout(() => { window.location.href = res.data.url; }, 900);
 return;
 }
 setError("We couldn't start your checkout. Please try again or contact support.");
 } catch (err) {
 const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
 setError(msg);
 } finally {
 setLoading(false);
 }
 };

 if (authLoading) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
 <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
 </div>
 );
 }

 return (
 <LanguageProvider>
 <div
 className="min-h-screen w-full relative overflow-hidden"
 data-testid="complete-subscription-page"
 style={{
 background:
 'radial-gradient(140% 90% at 50% -10%, #faf6ee 0%, #f7f1e4 30%, #f3ecdb 65%, #efe6cf 100%)',
 }}
 >
 <SEO
 title="Complete your subscription"
 description="Finish setting up your NoFee restaurant account. Secure Stripe checkout — 45-day free trial."
 path="/complete-subscription"
 noindex
 />

 {/* Subtle dual gold glow */}
 <div
 className="pointer-events-none absolute inset-0 opacity-70"
 style={{
 background:
 'radial-gradient(55% 50% at 90% 12%, rgba(212,175,55,0.14) 0%, transparent 60%), radial-gradient(45% 40% at 8% 88%, rgba(212,175,55,0.10) 0%, transparent 60%)',
 }}
 />

 {redirecting && <StripeRedirectOverlay message="Activating your restaurant — connecting to Stripe…" />}

 {/* Consistent Navbar */}
 <Navbar forceSolid />

 <main className="relative z-10 px-5 sm:px-8 lg:px-12 pb-20 pt-28 sm:pt-32">
 <div className="w-full max-w-[1240px] mx-auto">
 <button
 type="button"
 onClick={() => navigate(-1)}
 data-testid="complete-subscription-back"
 className="mb-5 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-900/15 hover:border-zinc-900/30 bg-white/40 backdrop-blur-sm text-zinc-700 hover:text-zinc-900 text-[12.5px] font-semibold transition-all"
 >
 <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.3} /> Back
 </button>
 <OnboardingProgress currentStep="subscribe" className="mb-10" />
 </div>
 <div className="w-full max-w-[1240px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14">

 {/* ────────── LEFT — Emotional / Value ────────── */}
 <section className="order-2 lg:order-1 max-w-[600px]">
 {fromEmail && (
 <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-50/70 backdrop-blur-sm">
 <Check className="w-3.5 h-3.5 text-emerald-700" />
 <span className="text-[11.5px] uppercase tracking-[0.18em] text-emerald-800 font-bold">
 Welcome back
 </span>
 </div>
 )}

 {/* Founding Partner badge */}
 <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold-500/40 bg-gradient-to-r from-gold-500/[0.06] to-amber-500/[0.06] backdrop-blur-sm mb-6">
 <Crown className="w-3.5 h-3.5 text-gold-600" />
 <span className="text-[11px] uppercase tracking-[0.22em] text-gold-800 font-bold">
 Founding Restaurant Partner
 </span>
 </div>

 <h1
 className="text-[44px] sm:text-[56px] lg:text-[64px] font-black tracking-[-0.025em] leading-[1.0] text-neutral-900"
 data-testid="complete-headline"
 >
 45 Days
 <br />
 <span className="bg-gradient-to-r from-amber-700 via-gold-600 to-amber-800 bg-clip-text text-transparent">
 Free.
 </span>
 </h1>

 <p className="mt-5 text-[18px] sm:text-[19px] text-neutral-700 leading-[1.55] max-w-[520px] font-medium">
 {restaurant?.name? `${restaurant.name} — `: ''}
 keep more profit every month with <span className="text-neutral-900 font-bold">0% commission</span>.
 Activate your restaurant and start receiving orders today.
 </p>

 {/* Benefits */}
 <ul className="mt-9 space-y-3" data-testid="complete-benefits">
 {FOUNDING_BENEFITS.map((b) => (
 <li key={b} className="flex items-start gap-3 text-[15px] text-neutral-700">
 <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/35 flex items-center justify-center">
 <Check className="w-3 h-3 text-emerald-700" strokeWidth={3} />
 </span>
 <span>{b}</span>
 </li>
 ))}
 </ul>

 {/* ──────────────────────────────────────────────────────────
 FAST BY NOFEE — sales pitch shown right above the payment
 form (2026-06-09 spec). Contrasts intentionally against the
 cream/light surface using NoFee's black + gold language so
 owners notice it before completing checkout.
 ────────────────────────────────────────────────────────── */}
 <section
 data-testid="complete-fast-by-nofee-section"
 className="relative mt-10 rounded-3xl border border-yellow-500/40 bg-gradient-to-br from-black via-zinc-950 to-black p-6 sm:p-8 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.45)] overflow-hidden"
 >
 <div
 aria-hidden="true"
 className="pointer-events-none absolute -top-20 -right-16 w-[320px] h-[320px] rounded-full bg-yellow-500/[0.10] blur-3xl"
 />
 <div
 aria-hidden="true"
 className="pointer-events-none absolute -bottom-24 -left-12 w-[280px] h-[280px] rounded-full bg-amber-500/[0.06] blur-3xl"
 />

 <div className="relative">
 {/* Header pill */}
 <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 text-[10.5px] font-extrabold uppercase tracking-[0.18em]">
 <Zap className="w-3 h-3 fill-yellow-300" /> Bonus program · Free with Ordering
 </div>

 <h3
 data-testid="complete-fast-title"
 className="mt-4 font-['Outfit',_Inter,_sans-serif] text-[24px] sm:text-[28px] font-semibold tracking-tight text-white flex items-center gap-2 flex-wrap"
 >
 <Zap className="w-6 h-6 text-yellow-300 fill-yellow-300" />
 <span>FAST BY NOFEE</span>
 </h3>
 <p className="mt-2 text-[14px] sm:text-[15px] text-yellow-200/85 font-semibold">
 Get more orders through verified delivery performance.
 </p>
 <p className="mt-3 text-[13px] text-white/65 leading-relaxed">
 Customers recognize reliable restaurants and are more likely to order
 from businesses that consistently deliver on time.
 </p>

 <ul
 data-testid="complete-fast-benefits"
 className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5"
 >
 {[
 { Icon: ShieldCheck, text: 'Increase customer confidence' },
 { Icon: TrendingUp,  text: 'Improve restaurant credibility' },
 { Icon: Check,       text: 'Highlight reliable delivery performance' },
 { Icon: Repeat,      text: 'Encourage repeat customers' },
 { Icon: Star,        text: 'Strengthen customer trust' },
 { Icon: Award,       text: 'Fast Score available inside your dashboard' },
 ].map((b, i) => (
 <li key={i} className="flex items-start gap-2.5 text-[13px] text-white/85 leading-relaxed">
 <span className="mt-0.5 w-5 h-5 rounded-md bg-yellow-500/15 border border-yellow-500/35 flex items-center justify-center shrink-0">
 <b.Icon className="w-3 h-3 text-yellow-300" strokeWidth={2.4} />
 </span>
 <span>{b.text}</span>
 </li>
 ))}
 </ul>

 {/* Highlight message in gold */}
 <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-yellow-500/40 bg-yellow-500/[0.08]">
 <Zap className="w-4 h-4 text-yellow-300 fill-yellow-300 shrink-0" />
 <p
 data-testid="complete-fast-highlight"
 className="text-[12.5px] sm:text-[13px] font-bold text-yellow-300 leading-snug"
 >
 Fast by NoFee does not mean the fastest delivery. It means: Delivered On Time. Verified by Real Customers.
 </p>
 </div>

 <button
 type="button"
 onClick={() => setFastInfoOpen(true)}
 data-testid="complete-fast-learn-more-btn"
 className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-yellow-500/45 bg-black/40 hover:bg-yellow-500/[0.08] text-yellow-200 hover:text-yellow-100 font-bold text-[12.5px] tracking-tight transition-all"
 >
 <Sparkles className="w-3.5 h-3.5" />
 Learn More About Fast
 <ArrowRight className="w-3.5 h-3.5" />
 </button>
 </div>
 </section>

 {/* Scarcity / exclusivity line */}
 <div className="mt-8 inline-flex items-center gap-2 text-[12.5px] text-neutral-600">
 <Sparkles className="w-3.5 h-3.5 text-gold-600" />
 <span><span className="font-bold text-neutral-800">Limited founding partner access</span> — priority support included.</span>
 </div>

 {/* Dashboard preview */}
 <div className="mt-10 hidden sm:block">
 <DashboardPreview />
 </div>
 </section>

 {/* ────────── RIGHT — Action card ────────── */}
 <section className="order-1 lg:order-2 w-full">
 <div
 className="relative bg-white/85 backdrop-blur-xl rounded-[28px] border border-white/70 p-7 sm:p-9 shadow-[0_40px_100px_-25px_rgba(120,80,20,0.22),0_10px_30px_-10px_rgba(0,0,0,0.08)]"
 data-testid="complete-plan-card"
 >
 {/* Plan header */}
 <div className="mb-6">
 <div className="flex items-start justify-between gap-4">
 <div>
 <p className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">
 Your selected plan
 </p>
 <p className="mt-1 text-[15px] text-neutral-700 font-bold">{plan.label}</p>
 </div>
 <div className="text-right">
 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-50">
 <Clock className="w-3 h-3 text-emerald-700" />
 <span className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-800 font-bold">
 {plan.trialDays}-day free trial
 </span>
 </div>
 <p className="mt-2 text-[11.5px] text-neutral-500">No charge today</p>
 </div>
 </div>

 {/* Price block — Founding Partner pricing breakdown */}
 <div
 className="mt-5 rounded-2xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.06] via-amber-500/[0.04] to-transparent p-5"
 data-testid="complete-price-block"
 >
 <div className="flex items-baseline gap-2.5">
 <span className="text-[42px] font-black tracking-[-0.02em] text-neutral-900 leading-none" data-testid="founding-price">
 ${plan.foundingPrice}
 </span>
 <span className="text-[14px] text-neutral-600 font-semibold">/month</span>
 <span className="ml-1 text-[15px] text-neutral-400 line-through font-medium" data-testid="regular-price-strike">
 ${plan.regularPrice}
 </span>
 </div>
 <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gold-500/15 border border-gold-500/40">
 <Crown className="w-3 h-3 text-gold-700" />
 <span className="text-[10.5px] uppercase tracking-[0.18em] text-gold-800 font-bold">
 Save ${plan.regularPrice - plan.foundingPrice}/mo · First {plan.foundingMonths} months
 </span>
 </div>
 <div className="mt-4 space-y-1.5 text-[12.5px] text-neutral-700 leading-relaxed">
 <div className="flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
 <span><span className="font-bold text-neutral-900">{plan.trialDays} days free</span> — no charge today, cancel anytime</span>
 </div>
 <div className="flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
 <span><span className="font-bold text-neutral-900">${plan.foundingPrice}/month</span> for your first {plan.foundingMonths} months</span>
 </div>
 <div className="flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
 <span>Then <span className="font-bold text-neutral-900">${plan.regularPrice}/month</span> · cancel anytime in one click</span>
 </div>
 </div>
 </div>
 </div>

 {error && (
 <div
 className="mb-4 p-3 bg-red-50 text-red-700 text-[13px] rounded-2xl border border-red-200 flex items-start gap-2"
 data-testid="complete-error"
 >
 <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{error}</span>
 </div>
 )}

 {/* Premium CTA */}
 <button
 type="button"
 onClick={handleResume}
 disabled={loading || redirecting}
 data-testid="complete-resume-btn"
 className="group relative w-full h-[62px] inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-neutral-900 to-black text-white font-extrabold tracking-[0.01em] text-[16px] transition-all duration-300 shadow-[0_14px_36px_-10px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_20px_50px_-14px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.16)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 overflow-hidden"
 >
 <span
 className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
 style={{
 background:
 'linear-gradient(120deg, transparent 25%, rgba(212,175,55,0.22) 50%, transparent 75%)',
 }}
 />
 <span className="relative inline-flex items-center gap-2">
 <Crown className="w-4 h-4 text-gold-400" />
 {loading? 'Preparing checkout…': redirecting? 'Activating…': 'Activate My Restaurant'}
 </span>
 {!loading &&!redirecting && (
 <ArrowRight className="relative w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
 )}
 </button>

 {/* Trust microcopy */}
 <div className="mt-4 flex items-center justify-center gap-2">
 <Lock className="w-3.5 h-3.5 text-neutral-500" />
 <p className="text-[12px] text-neutral-600">
 Trusted secure checkout powered by{' '}
 <span className="font-bold text-neutral-800">Stripe</span>
 </p>
 </div>

 {/* Transparent billing summary line */}
 <p
 className="mt-2 text-center text-[11.5px] text-neutral-500 leading-relaxed"
 data-testid="billing-transparency"
 >
 45 days free, then ${plan.foundingPrice}/mo for {plan.foundingMonths} months, then ${plan.regularPrice}/mo. Cancel anytime.
 </p>

 {/* Compliance strip */}
 <div className="mt-5 pt-5 border-t border-neutral-200/70 grid grid-cols-3 gap-2 text-center">
 {[
 ['PCI', 'Level 1'],
 ['256-bit', 'TLS encrypted'],
 ['SSL', 'Secured'],
 ].map(([k, v]) => (
 <div key={k}>
 <p className="text-[11px] font-black text-neutral-800 tracking-tight">{k}</p>
 <p className="text-[10px] text-neutral-500 uppercase tracking-wider font-semibold mt-0.5">{v}</p>
 </div>
 ))}
 </div>

 {/* Payment networks strip */}
 <div className="mt-5 pt-5 border-t border-neutral-200/70">
 <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-3 text-center">
 Accepted payment methods
 </p>
 <PaymentNetworkRow className="justify-center" logoClass="h-5 sm:h-6 w-auto opacity-90" />
 </div>
 </div>

 {/* FAQ */}
 <div className="mt-8">
 <p className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-3 px-1">
 Quick answers
 </p>
 <FaqAccordion />
 </div>

 {/* Bottom payment partners */}
 <div className="mt-8 flex items-center justify-center gap-6 sm:gap-8 opacity-90">
 <StripeWordmark className="h-6 w-auto" />
 </div>
 <p className="mt-3 text-center text-[11px] text-neutral-500">
 By continuing you agree to the{' '}
 <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-700 font-semibold hover:text-gold-700 hover:underline transition-colors">
 Terms
 </a>{' '}
 &{' '}
 <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-700 font-semibold hover:text-gold-700 hover:underline transition-colors">
 Privacy
 </a>
.
 </p>
 </section>
 </div>
 </main>
 {fastInfoOpen && (
 <FastInfoModal
 onClose={() => setFastInfoOpen(false)}
 />
 )}
 </div>
 </LanguageProvider>
 );
};

export default CompleteSubscriptionPage;
